import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, PhoneIncoming, PhoneOutgoing, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { plannedActivitiesApi, callOutcomesApi } from "../../lib/plannedActivitiesApi";
import TimeSlotPicker from "./TimeSlotPicker";
import AssigneeMultiPicker from "./AssigneeMultiPicker";
import { nameInitials } from "../../lib/nameInitials";

/**
 * Görüşme Kaydı dialog — logs a phone call that already happened.
 *
 * Fields mirror the ExproCRM reference screenshot:
 *   - Arama Tipi (giden / gelen) toggle
 *   - Görüşme Tarihi + Saati
 *   - Görüşme Süresi (dakika)
 *   - Görüşülen Kişi (contact people scoped to the tenant)
 *   - Arama Sonucu (call outcome taxonomy) + inline "+ Yeni Ekle"
 *   - Görüşme Özeti (free-form notes)
 */
export default function CallLogDialog({
  open,
  onClose,
  constructionId,
  contacts,
  companies,
  defaultCompanyId,
  users,
  currentUserId,
  onCreated,
}) {
  const [direction, setDirection] = useState("outgoing");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState(() => {
    // Snap current time to the nearest 15-min slot
    const d = new Date();
    const q = Math.round(d.getMinutes() / 15) % 4;
    return `${String(d.getHours()).padStart(2, "0")}:${String(q * 15).padStart(2, "0")}`;
  });
  const [durationMin, setDurationMin] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [contactId, setContactId] = useState("");
  const [outcomeId, setOutcomeId] = useState("");
  const [outcomes, setOutcomes] = useState([]);
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [addOutcomeOpen, setAddOutcomeOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    callOutcomesApi
      .list()
      .then(setOutcomes)
      .catch(() => setOutcomes([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Reset form each open
    setDirection("outgoing");
    setDate(new Date().toISOString().slice(0, 10));
    const d = new Date();
    const q = Math.round(d.getMinutes() / 15) % 4;
    setTime(`${String(d.getHours()).padStart(2, "0")}:${String(q * 15).padStart(2, "0")}`);
    setDurationMin("");
    setCompanyId(defaultCompanyId || "");
    setContactId("");
    setOutcomeId("");
    setAssigneeIds(currentUserId ? [currentUserId] : []);
    setSummary("");
  }, [open, currentUserId, defaultCompanyId]);

  const handleSubmit = async () => {
    if (!date || !time) {
      toast.error("Tarih ve saat zorunlu");
      return;
    }
    setSaving(true);
    try {
      const scheduledAt = new Date(`${date}T${time}:00`).toISOString();
      const doc = await plannedActivitiesApi.create(constructionId, {
        kind: "call",
        call_direction: direction,
        scheduled_at: scheduledAt,
        call_duration_minutes: durationMin ? parseInt(durationMin, 10) : null,
        call_person_contact_id: contactId || null,
        call_outcome_id: outcomeId || null,
        assigned_user_ids: assigneeIds,
        description: summary,
      });
      toast.success("Görüşme kaydı oluşturuldu");
      onCreated?.(doc);
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Kayıt oluşturulamadı"
      );
    } finally {
      setSaving(false);
    }
  };

  const reachedOutcomes = outcomes.filter((o) => o.category === "reached");
  const notReachedOutcomes = outcomes.filter(
    (o) => o.category === "not_reached"
  );

  // Persons scoped to the chosen company. When no company is selected we
  // fall back to the full list so power users can still pick anyone.
  const filteredContacts = companyId
    ? (contacts || []).filter((c) => c.company_id === companyId)
    : contacts || [];

  // Auto-clear the person choice when the company changes to prevent
  // stale references (e.g. picking Cisco after already choosing an
  // Ericsson employee).
  useEffect(() => {
    if (companyId && contactId) {
      const person = (contacts || []).find((c) => c.id === contactId);
      if (person && person.company_id !== companyId) {
        setContactId("");
      }
    }
  }, [companyId, contactId, contacts]);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-2xl" data-testid="call-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-sky-100 text-sky-600">
                <Phone className="h-4 w-4" />
              </span>
              Görüşme Kaydı
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="mb-2 block text-xs font-semibold text-zinc-700">
                Arama Tipi *
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setDirection("outgoing")}
                  data-testid="call-direction-outgoing"
                  className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                    direction === "outgoing"
                      ? "border-sky-400 bg-sky-50 text-sky-800"
                      : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
                  }`}
                >
                  <PhoneOutgoing className="h-4 w-4" />
                  Giden Arama
                </button>
                <button
                  type="button"
                  onClick={() => setDirection("incoming")}
                  data-testid="call-direction-incoming"
                  className={`inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors ${
                    direction === "incoming"
                      ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                      : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
                  }`}
                >
                  <PhoneIncoming className="h-4 w-4" />
                  Gelen Arama
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="call-date" className="text-xs font-semibold text-zinc-700">
                  Görüşme Tarihi *
                </Label>
                <Input
                  id="call-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="call-date-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">
                  Görüşme Saati *
                </Label>
                <div className="mt-1">
                  <TimeSlotPicker
                    value={time}
                    onChange={setTime}
                    testId="call-time"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-700">
                  Şirket
                </Label>
                <Select
                  value={companyId || "__none__"}
                  onValueChange={(v) => setCompanyId(v === "__none__" ? "" : v)}
                >
                  <SelectTrigger className="mt-1" data-testid="call-company-select">
                    <SelectValue placeholder="Şirket seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Tüm şirketler —</SelectItem>
                    {(companies || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name || "İsimsiz şirket"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">
                  Görüşülen Kişi
                </Label>
                <Select value={contactId || "__none__"} onValueChange={(v) => setContactId(v === "__none__" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="call-contact-select">
                    <SelectValue placeholder={companyId ? "Kişi seçin" : "Önce şirket seçin"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Kimse seçilmedi —</SelectItem>
                    {filteredContacts.length === 0 ? (
                      <div className="px-2 py-3 text-center text-[11px] text-zinc-400">
                        {companyId
                          ? "Bu şirkete bağlı kişi yok"
                          : "Kişi listesi boş"}
                      </div>
                    ) : (
                      filteredContacts.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {[c.first_name, c.last_name].filter(Boolean).join(" ") ||
                            c.email ||
                            "İsimsiz kişi"}
                          {c.title && (
                            <span className="ml-1.5 text-[10px] text-zinc-500">
                              · {c.title}
                            </span>
                          )}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="call-duration" className="text-xs font-semibold text-zinc-700">
                Görüşme Süresi (dakika)
              </Label>
              <Input
                id="call-duration"
                type="number"
                min="0"
                placeholder="Örn: 15"
                value={durationMin}
                onChange={(e) => setDurationMin(e.target.value)}
                data-testid="call-duration-input"
                className="mt-1 max-w-[200px]"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-zinc-700">
                Sorumlu (kime atansın?)
              </Label>
              <div className="mt-1">
                <AssigneeMultiPicker
                  value={assigneeIds}
                  onChange={setAssigneeIds}
                  users={users}
                  testId="call-assignee"
                  placeholder="Sorumlu atanmadı"
                />
              </div>
              <p className="mt-1 text-[10px] text-zinc-500">
                Hatırlatma bu kişilerin gelen kutusuna düşer. Boş bırakılırsa oluşturana gider.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-zinc-700">
                  Arama Sonucu
                </Label>
                <button
                  type="button"
                  onClick={() => setAddOutcomeOpen(true)}
                  data-testid="call-outcome-add-btn"
                  className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-medium text-violet-700 hover:bg-violet-50"
                >
                  <Plus className="h-3 w-3" />
                  Yeni Ekle
                </button>
              </div>
              <Select value={outcomeId} onValueChange={setOutcomeId}>
                <SelectTrigger className="mt-1" data-testid="call-outcome-select">
                  <SelectValue placeholder="Arama sonucu seçin" />
                </SelectTrigger>
                <SelectContent>
                  {reachedOutcomes.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Görüşüldü</SelectLabel>
                      {reachedOutcomes.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                  {notReachedOutcomes.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Görüşülemedi</SelectLabel>
                      {notReachedOutcomes.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="call-summary" className="text-xs font-semibold text-zinc-700">
                Görüşme Özeti
              </Label>
              <Textarea
                id="call-summary"
                rows={4}
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Görüşme detaylarını buraya yazın..."
                data-testid="call-summary-input"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="call-log-cancel"
            >
              <X className="mr-1 h-4 w-4" /> İptal
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              data-testid="call-log-submit"
              className="bg-sky-600 text-white hover:bg-sky-700"
            >
              <Phone className="mr-1 h-4 w-4" /> Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddOutcomeDialog
        open={addOutcomeOpen}
        onClose={() => setAddOutcomeOpen(false)}
        onCreated={(newOne) => {
          setOutcomes((prev) => [...prev, newOne]);
          setOutcomeId(newOne.id);
        }}
      />
    </>
  );
}

/**
 * Nested "+ Yeni Ekle" dialog — inline creation of a new call outcome
 * without leaving the parent Görüşme Kaydı form.
 */
function AddOutcomeDialog({ open, onClose, onCreated }) {
  const [category, setCategory] = useState("reached");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory("reached");
    setLabel("");
  }, [open]);

  const handleSubmit = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      toast.error("Sonuç adı zorunlu");
      return;
    }
    setSaving(true);
    try {
      const doc = await callOutcomesApi.create({ category, label: trimmed });
      toast.success("Yeni sonuç eklendi");
      onCreated?.(doc);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-outcome-dialog">
        <DialogHeader>
          <DialogTitle>Yeni Arama Sonucu Ekle</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              Kategori
            </Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="add-outcome-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reached">Görüşüldü</SelectItem>
                <SelectItem value="not_reached">Görüşülemedi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="new-outcome-label" className="text-xs font-semibold text-zinc-700">
              Sonuç Adı *
            </Label>
            <Input
              id="new-outcome-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Örnek: Toplantı planlandı"
              data-testid="add-outcome-label-input"
              className="mt-1"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="add-outcome-cancel">
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="add-outcome-submit"
            className="bg-violet-600 text-white hover:bg-violet-700"
          >
            <Plus className="mr-1 h-4 w-4" /> Ekle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
